'use client'

import {Button, Checkbox, Form, Input, Locale, SanityBlockContent, SmallText} from '@local/ui/components'
import {PortableTextBlock} from '@portabletext/types'
import {cx} from '@local/utils/cx'
import {useEffect, useId, useRef, useState} from 'react'
import {EMAIL_PATTERN, emailIsValid} from '@local/utils/string'

import SpinnerSVG from '@local/assets/svgs/spinner.svg'
import CheckSVG from '@local/assets/svgs/check.svg'
import ErrorSVG from '@local/assets/svgs/input-error.svg'

export interface NewsletterFormProps {
	listID: string
	title?: string
	terms: PortableTextBlock[]
	successMessage: string
	successClassName?: string
	formClassName?: string
	className?: string
	hasSurtitle?: boolean
	textColor?: 'text-white' | 'text-black'
	onSuccess?: () => void
}

export const NewsletterForm = ({
	listID,
	title,
	terms,
	className,
	successMessage,
	successClassName,
	formClassName,
	hasSurtitle = true,
	textColor = 'text-white',
	onSuccess,
}: NewsletterFormProps) => {
	const inputRef = useRef<HTMLInputElement>(null)
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [isSuccess, setIsSuccess] = useState<boolean>(false)
	const [hasError, setHasError] = useState<boolean>(false)
	const [errorText, setErrorText] = useState<string>('')

	const key = useId()

	const handleSubmit = async ({body}) => {
		setIsLoading(true)
		try {
			setIsSuccess(false)
			setHasError(false)
			setErrorText('')

			if (!emailIsValid(body.email)) {
				throw new Error('InvalidEmail')
			}

			const res = await fetch('/api/newsletter/subscribe/', {
				method: 'POST',
				body: JSON.stringify({email_address: body.email, listID}),
			})
			const result = await res.json()

			if (res.status === 200) {
				setIsSuccess(true)
				setIsLoading(false)
				return {status: 200}
			} else {
				setIsLoading(false)
				setErrorText(result.message)
				setHasError(true)
				return {errors: [{...result, code: 400}]}
			}
		} catch (error) {
			setIsLoading(false)
			setHasError(true)

			if (error.message === 'InvalidEmail') {
				setErrorText('Enter a valid email.')
				return {errors: [{code: 400, message: 'Invalid email'}]}
			}
			if (error.message === 'RequestFailed') {
				setErrorText('Something went wrong.')
				return {errors: [{code: 500, message: 'Request failed'}]}
			}
		}
	}

	useEffect(() => {
		if (isSuccess) {
			setTimeout(() => {
				setIsSuccess(false)
				onSuccess?.()
			}, 5000)
		}
	}, [isSuccess])

	const handleAdditionalReset = () => {
		if (inputRef.current) {
			inputRef.current.reset()
			inputRef.current.blur()
		}
	}

	const handleInputChange = (e) => {
		setHasError(false)
	}

	if (!listID) return null

	return (
		<div className={className}>
			{hasSurtitle && <SmallText textColor='grey-1' className='pb-8 lg:span-w-4'><Locale>Newsletter</Locale></SmallText>}
			{title && <h2 className='font-serif text-24 lg:text-34 leading-none tracking-tight pb-30 lg:pb-50 lg:span-w-4'>{title}</h2>}
			<Form
				autoReset
				additionalReset={handleAdditionalReset}
				// @ts-expect-error handleSubmit can't decide between Promis and void
				onSubmit={handleSubmit}
				buttonTheme='dark'
				className={cx('lg:span-w-3', formClassName)}
				errorClassName='hidden'
				successMessage={successMessage}
				successClassName={cx(
					'px-14 pt-16 pb-24 !mt-0',
					'bg-grey-footer text-12 tracking-widest text-white',
					'flex items-center h-[calc(100%+10px)] bottom-0 z-2',
					successClassName,
				)}>

				<div className={cx('relative w-full')}>
					<Input
						type='email'
						name='email'
						id={`newsletter-email-${key}`}
						label='Email Address'
						aria-autocomplete='none'
						autoComplete='off'
						ref={inputRef}
						onInvalid={() => setHasError(true)}
						pattern={EMAIL_PATTERN}
						variant={textColor === 'text-white' ? 'dark' : 'light'}
						hasError={hasError}
						errorMessage={hasError ? errorText : ''}
						onChange={handleInputChange}
						errorClassName={cx(
							'z-3 opacity-100',
							textColor === 'text-white' && '!bg-grey-footer',
							textColor === 'text-black' && '!bg-white',
						)}
						inputClassName='pl-20 pr-85'
						labelClassName={cx(
							'peer-focus:text-8',
							textColor === 'text-white' && '!bg-grey-footer',
							textColor === 'text-black' && '!bg-white',
						)} />
					<Button
						className={cx(
							'absolute right-2 bottom-3 p-8 pr-20 h-38 lg:h-42 lg:bottom-0',
							'text-10 uppercase leading-narrow tracking-widest',
						)}
						type='submit'
						loading={isLoading}>
						{!isLoading && !isSuccess && !hasError && <Locale>Confirm</Locale>}
						{isLoading && !isSuccess && <SpinnerSVG className='w-10 animate-spin' fill='currentColor' />}
						{!isLoading && isSuccess && !hasError && <CheckSVG className='w-10' />}
						{!isLoading && !isSuccess && hasError && <ErrorSVG />}
					</Button>
				</div>

				<input type='submit' hidden />

				<div className='mt-10'>
					<Checkbox name='privacy-check' id={`privacy-check-${key}`} required theme='dark' className={textColor}>
						<SanityBlockContent
							blocks={terms}
							styles={{
								normal: cx(
									'text-11 m-0 [&>a]:underline [&>a]:normal-case [&>a]:tracking-normal [&>a]:font-normal',
								),
							}} />
					</Checkbox>
				</div>
				<input type='hidden' value={listID} name='listID' id={`list-id-${key}`} />
			</Form>
		</div>
	)
}
